import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { type SupportedCurrency, detectUserCurrency, formatPrice } from '../lib/currency';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

interface CurrencyContextValue {
  currency: SupportedCurrency;
  setCurrency: (c: SupportedCurrency) => void;
  format: (amount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currency: 'BRL',
  setCurrency: () => {},
  format: n => formatPrice(n, 'BRL'),
});

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [currency, setCurrencyState] = useState<SupportedCurrency>('BRL');

  // On mount: use profile preference, then detect
  useEffect(() => {
    if ((profile?.currency as SupportedCurrency | undefined)) {
      setCurrencyState(profile!.currency as SupportedCurrency);
    } else {
      setCurrencyState(detectUserCurrency());
    }
  }, [profile?.currency]);

  async function setCurrency(c: SupportedCurrency) {
    setCurrencyState(c);
    if (profile?.id) {
      await supabase.from('profiles').update({ currency: c }).eq('id', profile.id);
    }
  }

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, format: n => formatPrice(n, currency) }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
